import days from 'dayjs';

import { C_value } from '@app/shared/_models/common';
import { DelrunsService } from '@app/logistic/delruns/delruns.service';
import { sales_order_mode } from '@app/shared/constants';
import { hasSignedBlPdf } from '@app/sales/rental-billing/rental-line-docs';
import { parisDay } from '../rental-contract.model';

// BL tels que les rend GET /deliveries?q=id_order:N AND limit:1000&o=lines,attachs (derniers par id), tournée et pièces comprises.
export interface BlIn {
  id: number; mode: number; askdatdel?: string | null; askslot?: number | null; realdatdel?: string | null;
  id_del_run?: number | null; seq_run?: number | null; lnk_deliveryl?: { id_order_l: number; qty: number }[] | null; lnk_attachs?: unknown[] | null;
}
export interface BlLineIn { id?: number | null; typ?: string | null; memo?: string | null; }

export interface RentalBl {
  id: number;
  signed: boolean;
  day: string | null;          // jour demandé (askdatdel), celui où le board des tournées le range
  half: 0 | 1 | 2;             // demi-journée du créneau (c_values 8) : 0 = journée entière
  signedAt: string | null;     // realdatdel, instant
  signedPdf: boolean;          // PDF d'archivage rangé à la signature ; absent pour un BL réalisé avant l'archivage
  what: string;                // « 1 reprise · 2 livraisons »
  patients: string;            // mémos des lignes lues, 3 au plus
  run: number | null;          // tournée affectée, arrêt seq_run
  stop: number | null;
  planning: string[] | null;   // segments du lien vers le board ; null pour Emportée, Retrait magasin ou sans date
  mode: number;
  modeLabel: string;
}

export const SIGNED_DAYS = 30;

const plural = (n: number, one: string, many: string) => (n ? `${n} ${n > 1 ? many : one}` : '');

// Non signés (hors bon d'annulation) par jour demandé, sans date en dernier ; signés depuis SIGNED_DAYS jours, du plus récent au plus ancien.
export function rentalBlsOf(dels: BlIn[], lines: BlLineIn[], slots: C_value[], today: string): { open: RentalBl[]; signed: RentalBl[] } {
  const byId = new Map(lines.filter((l) => Number(l.id) > 0).map((l) => [Number(l.id), l]));
  const since = days(today).subtract(SIGNED_DAYS, 'day').format('YYYY-MM-DD');
  const all = dels.filter((d) => Number(d.mode) >= 0).map((d) => blOf(d, byId, slots));
  const open = all.filter((b) => !b.signed).sort((a, b) => (a.day ?? '\uffff').localeCompare(b.day ?? '\uffff') || a.id - b.id);
  const signed = all.filter((b) => b.signed && (parisDay(b.signedAt) ?? '') >= since).sort((a, b) => String(b.signedAt).localeCompare(String(a.signedAt)) || b.id - a.id);
  return { open, signed };
}

// Lecture plafonnée (par id décroissant) à l'un des `caps` possibles : la période n'est sûrement couverte que si le plus ancien BL lu la précède.
export function mayMiss(dels: BlIn[], today: string, caps: readonly number[]): boolean {
  if (!caps.includes(dels.length)) return false;
  const oldest = dels.reduce((a, b) => (Number(b.id) < Number(a.id) ? b : a));
  const day = parisDay(oldest.realdatdel) ?? parisDay(oldest.askdatdel);
  return !day || day >= days(today).subtract(SIGNED_DAYS, 'day').format('YYYY-MM-DD');
}

function blOf(d: BlIn, byId: Map<number, BlLineIn>, slots: C_value[]): RentalBl {
  // Reprise = ligne louée à −1 sur ce BL (JP 29/10), livraison = ligne louée posée ; le signe se garde avec la ligne.
  const moves = (d.lnk_deliveryl ?? []).filter((dl) => Number(dl.qty) !== 0).map((dl) => ({ back: Number(dl.qty) < 0, l: byId.get(Number(dl.id_order_l)) ?? null }));
  const mine = moves.map((x) => x.l);
  const count = (pred: (l: BlLineIn, back: boolean) => boolean) => moves.filter((x) => x.l && pred(x.l, x.back)).length;
  const what = [
    plural(count((l, back) => l.typ === 'RENTAL' && !back), 'livraison', 'livraisons'),
    plural(count((l, back) => (l.typ === 'RENTAL' && back) || l.typ === 'RENTAL_RETURN'), 'reprise', 'reprises'),   // RENTAL_RETURN : BL d'avant la règle
    plural(count((l) => l.typ !== 'RENTAL' && l.typ !== 'RENTAL_RETURN'), 'article vendu', 'articles vendus'),
    plural(mine.filter((l) => !l).length, 'ligne de matériel repris', 'lignes de matériel repris'),   // page lue sans les lignes closes (o=open)
  ].filter((s) => !!s).join(' · ');
  const names = [...new Set(mine.map((l) => (l?.memo ?? '').trim()).filter((m) => !!m))];
  const day = parisDay(d.askdatdel);
  const mode = Number(d.mode) || 0;
  const half = DelrunsService.slotDayOf(d.askslot, slots);
  return {
    id: Number(d.id), signed: !!d.realdatdel, day, half: half === 1 || half === 2 ? half : 0, signedAt: d.realdatdel ?? null,
    signedPdf: !!d.realdatdel && hasSignedBlPdf(d.lnk_attachs),
    what: what || 'aucune ligne', patients: names.slice(0, 3).join(', ') + (names.length > 3 ? ` +${names.length - 3}` : ''),
    run: Number(d.id_del_run) || null, stop: Number(d.seq_run) || null,
    planning: DelrunsService.boardLinkOf(day, d.askslot, mode, slots),
    mode, modeLabel: sales_order_mode.get(mode) ?? '',
  };
}
